import {pickPayload} from './bowlCoverage.js'
import {getSessionGeneration} from './sessionGeneration.js'

// Local duplicate/stale-response fence, not server authorization or exactly-once delivery.
export function createDeskPickController({send, onState = () => {}, onPicked = () => {}, makeRequestId = () => globalThis.crypto?.randomUUID?.()}) {
  let active = true, pending = false, uncertain = false
  const session = getSessionGeneration()
  const current = () => active && session === getSessionGeneration()
  return {
    dispose() { active = false },
    async submit(input, candidates = []) {
      if (!current() || pending || uncertain) return false
      const body = pickPayload(input)
      if (body.error) { onState({status: 'error', msg: body.error}); return false }
      if (!candidates.some(candidate => candidate.short === body.rep)) {
        onState({status: 'error', msg: 'Choose a currently surfaced rep. Refresh the board if the roster changed.'})
        return false
      }
      if (!Number.isSafeInteger(input.expectedVersion) || input.expectedVersion < 0) {
        onState({status: 'error', msg: 'The current desk version is unavailable. Refresh the board before choosing.'})
        return false
      }
      if (!Number.isInteger(input.season) || input.season < 1900 || input.season > 2200) {
        onState({status:'error',msg:'The displayed bowl season is unavailable. Refresh the board before choosing.'})
        return false
      }
      const requestId = makeRequestId()
      if (typeof requestId !== 'string' || !requestId) {
        onState({status: 'error', msg: 'A unique request identity could not be created. No pick was sent.'})
        return false
      }
      body.season = input.season
      body.requestId = requestId
      body.expectedVersion = input.expectedVersion
      pending = true
      onState({status: 'saving', msg: ''})
      try {
        const result = await send(body)
        if (!current()) return false
        if (result?.ok !== true || result?.durable !== true || !result?.pick?.id || result.pick.requestId !== requestId || Number(result.pick.season) !== input.season) throw new Error('No matching saved-pick receipt was returned.')
        const pick = result.pick
        const conference = String(input.conference || '').trim()
        const repsMatch = Array.isArray(pick.reps) && (body.action === 'clear'
          ? pick.reps.length === 0 : pick.reps.length === 1 && pick.reps[0] === body.rep)
        if (pick.scope !== body.scope || pick.target !== (body.school || body.conference)
          || pick.action !== body.action || !repsMatch
          || (conference && pick.conference !== conference)) throw new Error('The saved-pick receipt does not match the reviewed school, conference and rep.')
        onState({status: 'done', msg: 'Pick saved. Refreshing the board to read the current desk.'})
        onPicked(result)
        return true
      } catch (error) {
        if (!current()) return false
        // Only explicit client/authority rejections are known not to have saved.
        uncertain = error?.body?.uncertain === true || error?.body?.retrySafe === false || ![400, 401, 403, 404, 409, 422].includes(error?.status)
        onState({status: uncertain ? 'uncertain' : 'error', msg: uncertain
          ? 'The save outcome is uncertain. Read recent desk picks and refresh the board before making another choice. Do not repeat this save blindly.'
          : error?.body?.detail || error?.body?.error || error?.message || 'The pick was refused.'})
        return false
      } finally { pending = false }
    },
  }
}
